import {
  AlertCircle,
  CalendarClock,
  CheckCircle2,
  Clock3,
  History,
  LoaderCircle,
  PencilLine,
  TimerReset,
  X,
} from "lucide-react";

import {
  useEffect,
  useMemo,
  useState,
  type FormEvent,
} from "react";

import {
  ApiRequestError,
  getTaskTime,
  updateTask,
} from "./api";

import {
  APPLICATION_TIME_ZONE_LABEL,
  formatSamaraDateTimeShort,
  isoToSamaraDateTimeInput,
  samaraLocalDateTimeToIso,
} from "./timezone";

import type {
  Project,
  Task,
  TaskPriority,
  TaskTimeSnapshot,
} from "./types";

const PRIORITY_LABELS: Record<
  TaskPriority,
  string
> = {
  low: "Низкий",
  normal: "Обычный",
  high: "Высокий",
  urgent: "Срочный",
};

const STATUS_LABELS: Record<
  Task["status"],
  string
> = {
  pending: "Ожидает",
  in_progress: "В работе",
  waiting: "На паузе",
  completed: "Выполнена",
  failed: "Провалена",
};

function formatDuration(
  totalSeconds: number,
): string {
  const seconds = Math.max(
    0,
    Math.floor(totalSeconds),
  );

  const hours = Math.floor(seconds / 3600);

  const minutes = Math.floor(
    (seconds % 3600) / 60,
  );

  const remainingSeconds =
    seconds % 60;

  if (hours > 0) {
    return [
      String(hours).padStart(2, "0"),
      String(minutes).padStart(2, "0"),
      String(remainingSeconds)
        .padStart(2, "0"),
    ].join(":");
  }

  return [
    String(minutes).padStart(2, "0"),
    String(remainingSeconds)
      .padStart(2, "0"),
  ].join(":");
}

function getErrorMessage(
  error: unknown,
): string {
  if (error instanceof ApiRequestError) {
    if (error.status === 401) {
      return (
        "Сессия завершена. Обнови страницу "
        + "и войди снова."
      );
    }

    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "Не удалось выполнить операцию.";
}

function TaskDetailsModal({
  task,
  projects,
  onClose,
  onUpdated,
}: {
  task: Task;
  projects: Project[];

  onClose: () => void;

  onUpdated:
    (updatedTask: Task) => Promise<void>;
}) {
  const [title, setTitle] =
    useState(task.title);

  const [description, setDescription] =
    useState(task.description ?? "");

  const [projectId, setProjectId] =
    useState(
      task.project
        ? String(task.project.id)
        : "",
    );

  const [priority, setPriority] =
    useState<TaskPriority>(
      task.priority,
    );

  const [
    plannedStartAt,
    setPlannedStartAt,
  ] = useState(
    isoToSamaraDateTimeInput(
      task.plannedStartAt,
    ),
  );

  const [
    plannedDurationMinutes,
    setPlannedDurationMinutes,
  ] = useState(
    task.plannedDurationMinutes === null
      ? ""
      : String(
          task.plannedDurationMinutes,
        ),
  );

  const [dueAt, setDueAt] =
    useState(
      isoToSamaraDateTimeInput(
        task.dueAt,
      ),
    );

  const [timeSnapshot, setTimeSnapshot] =
    useState<TaskTimeSnapshot | null>(
      null,
    );

  const [isLoadingHistory, setIsLoadingHistory] =
    useState(true);

  const [isSaving, setIsSaving] =
    useState(false);

  const [error, setError] =
    useState<string | null>(null);

  const [successMessage, setSuccessMessage] =
    useState<string | null>(null);

  const [clock, setClock] =
    useState(() => Date.now());

  useEffect(() => {
    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow =
      "hidden";

    function handleKeyDown(
      event: KeyboardEvent,
    ): void {
      if (event.key === "Escape") {
        onClose();
      }
    }

    window.addEventListener(
      "keydown",
      handleKeyDown,
    );

    return () => {
      document.body.style.overflow =
        previousOverflow;

      window.removeEventListener(
        "keydown",
        handleKeyDown,
      );
    };
  }, [onClose]);

  useEffect(() => {
    let isCancelled = false;

    async function loadHistory(): Promise<void> {
      setIsLoadingHistory(true);

      try {
        const response =
          await getTaskTime(task.id);

        if (!isCancelled) {
          setTimeSnapshot(
            response.data,
          );

          setError(null);
        }
      } catch (requestError: unknown) {
        if (!isCancelled) {
          setError(
            getErrorMessage(
              requestError,
            ),
          );
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingHistory(false);
        }
      }
    }

    void loadHistory();

    return () => {
      isCancelled = true;
    };
  }, [task.id]);

  useEffect(() => {
    if (!timeSnapshot?.timer.isRunning) {
      return;
    }

    const intervalId =
      window.setInterval(
        () => {
          setClock(Date.now());
        },
        1000,
      );

    return () => {
      window.clearInterval(intervalId);
    };
  }, [timeSnapshot?.timer.isRunning]);

  const liveActualDuration =
    useMemo(() => {
      if (!timeSnapshot) {
        return task.actualDurationSeconds;
      }

      let result =
        timeSnapshot.fact
          .actualDurationSeconds;

      if (
        timeSnapshot.timer.isRunning
        && timeSnapshot.timer
          .activeStartedAt
      ) {
        result += Math.max(
          0,
          Math.floor(
            (
              clock
              - new Date(
                  timeSnapshot.timer
                    .activeStartedAt,
                ).getTime()
            ) / 1000,
          ),
        );
      }

      return result;
    }, [
      clock,
      task.actualDurationSeconds,
      timeSnapshot,
    ]);

  async function handleSubmit(
    event: FormEvent<HTMLFormElement>,
  ): Promise<void> {
    event.preventDefault();

    if (
      isSaving
      || title.trim().length === 0
    ) {
      return;
    }

    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const plannedStartIso =
        samaraLocalDateTimeToIso(
          plannedStartAt,
        );

      const dueAtIso =
        samaraLocalDateTimeToIso(
          dueAt,
        );

      if (
        plannedStartIso
        && dueAtIso
        && new Date(dueAtIso).getTime()
          < new Date(
            plannedStartIso,
          ).getTime()
      ) {
        throw new Error(
          "Срок не может быть раньше начала задачи.",
        );
      }

      const durationText =
        plannedDurationMinutes.trim();

      const duration =
        durationText.length === 0
          ? null
          : Number(durationText);

      if (
        duration !== null
        && (
          !Number.isSafeInteger(duration)
          || duration < 1
          || duration > 10080
        )
      ) {
        throw new Error(
          "Длительность должна быть от 1 до 10080 минут.",
        );
      }

      const response = await updateTask(
        task.id,
        {
          projectId:
            projectId.length === 0
              ? null
              : Number(projectId),

          title: title.trim(),

          description:
            description.trim().length > 0
              ? description.trim()
              : null,

          priority,

          plannedStartAt:
            plannedStartIso,

          plannedDurationMinutes:
            duration,

          dueAt: dueAtIso,
        },
      );

      await onUpdated(
        response.data,
      );

      setSuccessMessage(
        "Изменения сохранены.",
      );
    } catch (requestError: unknown) {
      setError(
        getErrorMessage(requestError),
      );
    } finally {
      setIsSaving(false);
    }
  }

  const plannedSeconds =
    timeSnapshot?.plan
      .plannedDurationSeconds
      ?? (
        task.plannedDurationMinutes
          === null
          ? null
          : task.plannedDurationMinutes
              * 60
      );

  const deviationSeconds =
    plannedSeconds === null
      ? null
      : liveActualDuration
        - plannedSeconds;

  const completionPercent =
    plannedSeconds === null
    || plannedSeconds === 0
      ? null
      : Math.round(
          (
            liveActualDuration
            / plannedSeconds
          ) * 1000,
        ) / 10;

  return (
    <div
      className="details-modal-backdrop"
      role="presentation"
      onMouseDown={onClose}
    >
      <section
        className="details-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`Задача ${task.title}`}
        onMouseDown={(event) => {
          event.stopPropagation();
        }}
      >
        <header className="details-modal-header">
          <div className="details-modal-icon">
            <PencilLine size={23} />
          </div>

          <div className="details-modal-heading">
            <span>
              Задача #{task.id}
            </span>

            <h2>{task.title}</h2>

            <div className="details-header-badges">
              <span
                className={[
                  "details-status-badge",
                  `details-status-${task.status}`,
                ].join(" ")}
              >
                {STATUS_LABELS[task.status]}
              </span>

              <span className="details-timezone-badge">
                {APPLICATION_TIME_ZONE_LABEL}
              </span>
            </div>
          </div>

          <button
            type="button"
            className="details-modal-close"
            onClick={onClose}
            aria-label="Закрыть подробности задачи"
          >
            <X size={21} />
          </button>
        </header>

        <div className="details-modal-content">
          <form
            className="details-edit-panel"
            onSubmit={(event) => {
              void handleSubmit(event);
            }}
          >
            <div className="details-section-heading">
              <PencilLine size={18} />

              <div>
                <h3>Параметры задачи</h3>
                <p>
                  Все даты вводятся по Самаре.
                </p>
              </div>
            </div>

            {error && (
              <div
                className="details-message details-error"
                role="alert"
              >
                <AlertCircle size={18} />
                <span>{error}</span>
              </div>
            )}

            {successMessage && (
              <div
                className="details-message details-success"
                role="status"
              >
                <CheckCircle2 size={18} />
                <span>{successMessage}</span>
              </div>
            )}

            <label className="details-field">
              <span>
                Название
                <strong>*</strong>
              </span>

              <input
                type="text"
                value={title}
                onChange={(event) => {
                  setTitle(
                    event.target.value,
                  );

                  setSuccessMessage(null);
                }}
                maxLength={255}
                required
                disabled={isSaving}
              />
            </label>

            <label className="details-field">
              <span>Описание</span>

              <textarea
                value={description}
                onChange={(event) => {
                  setDescription(
                    event.target.value,
                  );

                  setSuccessMessage(null);
                }}
                rows={4}
                maxLength={10000}
                disabled={isSaving}
              />
            </label>

            <div className="details-form-grid">
              <label className="details-field">
                <span>Проект</span>

                <select
                  value={projectId}
                  onChange={(event) => {
                    setProjectId(
                      event.target.value,
                    );

                    setSuccessMessage(null);
                  }}
                  disabled={isSaving}
                >
                  <option value="">
                    Без проекта
                  </option>

                  {projects.map(
                    (project) => (
                      <option
                        key={project.id}
                        value={project.id}
                      >
                        {project.name}
                      </option>
                    ),
                  )}
                </select>
              </label>

              <label className="details-field">
                <span>Приоритет</span>

                <select
                  value={priority}
                  onChange={(event) => {
                    setPriority(
                      event.target
                        .value as TaskPriority,
                    );

                    setSuccessMessage(null);
                  }}
                  disabled={isSaving}
                >
                  {Object.entries(
                    PRIORITY_LABELS,
                  ).map(
                    ([
                      priorityValue,
                      priorityLabel,
                    ]) => (
                      <option
                        key={priorityValue}
                        value={priorityValue}
                      >
                        {priorityLabel}
                      </option>
                    ),
                  )}
                </select>
              </label>
            </div>

            <div className="details-timezone-note">
              <CalendarClock size={17} />

              <span>
                Часовой пояс:
                <strong>
                  {APPLICATION_TIME_ZONE_LABEL}
                </strong>
              </span>
            </div>

            <div className="details-form-grid">
              <label className="details-field">
                <span>Плановое начало</span>

                <input
                  type="datetime-local"
                  value={plannedStartAt}
                  onChange={(event) => {
                    setPlannedStartAt(
                      event.target.value,
                    );

                    setSuccessMessage(null);
                  }}
                  disabled={isSaving}
                />
              </label>

              <label className="details-field">
                <span>
                  Длительность, минут
                </span>

                <input
                  type="number"
                  value={
                    plannedDurationMinutes
                  }
                  onChange={(event) => {
                    setPlannedDurationMinutes(
                      event.target.value,
                    );

                    setSuccessMessage(null);
                  }}
                  min={1}
                  max={10080}
                  step={5}
                  disabled={isSaving}
                />
              </label>
            </div>

            <label className="details-field">
              <span>Срок выполнения</span>

              <input
                type="datetime-local"
                value={dueAt}
                onChange={(event) => {
                  setDueAt(
                    event.target.value,
                  );

                  setSuccessMessage(null);
                }}
                disabled={isSaving}
              />
            </label>

            <div className="details-form-actions">
              <button
                type="button"
                className="details-cancel-button"
                onClick={onClose}
                disabled={isSaving}
              >
                Закрыть
              </button>

              <button
                type="submit"
                className="details-save-button"
                disabled={
                  isSaving
                  || title.trim().length === 0
                }
              >
                {isSaving ? (
                  <>
                    <LoaderCircle
                      size={16}
                      className="rotating"
                    />

                    Сохраняем…
                  </>
                ) : (
                  <>
                    <CheckCircle2 size={16} />
                    Сохранить изменения
                  </>
                )}
              </button>
            </div>
          </form>

          <aside className="details-history-panel">
            <div className="details-section-heading">
              <History size={18} />

              <div>
                <h3>План и история</h3>
                <p>
                  Рабочие сессии задачи.
                </p>
              </div>
            </div>

            <div className="details-stat-grid">
              <article className="details-stat-card">
                <Clock3 size={18} />

                <span>План</span>

                <strong>
                  {plannedSeconds === null
                    ? "Не указан"
                    : formatDuration(
                        plannedSeconds,
                      )}
                </strong>
              </article>

              <article className="details-stat-card">
                <TimerReset size={18} />

                <span>Факт</span>

                <strong>
                  {formatDuration(
                    liveActualDuration,
                  )}
                </strong>
              </article>

              <article className="details-stat-card">
                <CalendarClock size={18} />

                <span>Отклонение</span>

                <strong
                  className={
                    deviationSeconds !== null
                    && deviationSeconds > 0
                      ? "details-over-plan"
                      : ""
                  }
                >
                  {deviationSeconds === null
                    ? "—"
                    : `${
                        deviationSeconds > 0
                          ? "+"
                          : ""
                      }${formatDuration(
                        Math.abs(
                          deviationSeconds,
                        ),
                      )}`}
                </strong>
              </article>

              <article className="details-stat-card">
                <CheckCircle2 size={18} />

                <span>Выполнение</span>

                <strong>
                  {completionPercent === null
                    ? "—"
                    : `${completionPercent}%`}
                </strong>
              </article>
            </div>

            <div className="details-task-dates">
              <div>
                <span>Первый запуск</span>

                <strong>
                  {formatSamaraDateTimeShort(
                    task.startedAt,
                  )}
                </strong>
              </div>

              <div>
                <span>Завершение</span>

                <strong>
                  {formatSamaraDateTimeShort(
                    task.completedAt
                    ?? task.failedAt,
                  )}
                </strong>
              </div>
            </div>

            <div className="details-history-heading">
              <span>Сессии</span>

              <strong>
                {timeSnapshot?.fact
                  .sessionCount ?? 0}
              </strong>
            </div>

            {isLoadingHistory ? (
              <div className="details-history-loading">
                <LoaderCircle
                  size={22}
                  className="rotating"
                />

                <span>
                  Загружаем историю…
                </span>
              </div>
            ) : (
              <div className="details-history-list">
                {timeSnapshot
                  && timeSnapshot.entries
                    .length > 0 ? (
                  timeSnapshot.entries.map(
                    (entry, index) => (
                      <article
                        key={entry.id}
                        className={[
                          "details-history-entry",
                          entry.isRunning
                            ? "details-history-running"
                            : "",
                        ].join(" ")}
                      >
                        <div className="details-history-number">
                          {
                            timeSnapshot.entries
                              .length - index
                          }
                        </div>

                        <div className="details-history-main">
                          <div>
                            <span>
                              {entry.isRunning
                                ? "Активная сессия"
                                : "Рабочая сессия"}
                            </span>

                            <strong>
                              {formatDuration(
                                entry.isRunning
                                  ? (
                                      entry
                                        .durationSeconds
                                      + Math.max(
                                          0,
                                          Math.floor(
                                            (
                                              clock
                                              - new Date(
                                                  entry
                                                    .startedAt,
                                                ).getTime()
                                            ) / 1000,
                                          ),
                                        )
                                    )
                                  : entry
                                      .durationSeconds,
                              )}
                            </strong>
                          </div>

                          <p>
                            Начало:{" "}
                            {formatSamaraDateTimeShort(
                              entry.startedAt,
                            )}
                          </p>

                          <p>
                            Окончание:{" "}
                            {entry.endedAt
                              ? formatSamaraDateTimeShort(
                                  entry.endedAt,
                                )
                              : "Сейчас работает"}
                          </p>
                        </div>
                      </article>
                    ),
                  )
                ) : (
                  <div className="details-history-empty">
                    <History size={27} />

                    <strong>
                      Сессий пока нет
                    </strong>

                    <span>
                      История появится после
                      запуска таймера.
                    </span>
                  </div>
                )}
              </div>
            )}
          </aside>
        </div>
      </section>
    </div>
  );
}

export default TaskDetailsModal;
